import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card } from "@/components/ui/card";
import { Share2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { useTheme } from 'next-themes';
import { getThemeAwareColor } from '@/utils/colorUtils';

import ShareNoteDialog from './ShareNoteDialog';

interface Note {
  id: string;
  title: string;
  content: string;
  color: string;
  cover_image?: string;
  group?: string;
  updated_at?: string;
  is_public?: boolean;
  share_id?: string;
}

interface DraggableNoteListProps {
  notes: Note[];
  onNoteClick: (note: Note) => void;
  onNotesReorder: (notes: Note[]) => void;
  onDelete: (id: string) => void;
}

const DraggableNoteList: React.FC<DraggableNoteListProps> = ({ 
  notes, 
  onNoteClick, 
  onNotesReorder,
  onDelete 
}) => {
  const { theme } = useTheme();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  const handleShare = (note: Note) => {
    setSelectedNote(note);
    setShareDialogOpen(true);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    const items = Array.from(notes);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    onNotesReorder(items);
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="notes">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
            >
              {notes.map((note, index) => {
                const themeAwareColor = getThemeAwareColor(note.color, theme === 'dark');
                return (
                  <Draggable key={note.id} draggableId={note.id} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <Card
                          className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer relative"
                          style={{ backgroundColor: themeAwareColor }}
                          onClick={() => onNoteClick(note)}
                        >
                        {note.cover_image && (
                          <div className="w-full h-48">
                            <img
                              src={note.cover_image}
                              alt={note.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-semibold mb-2">{note.title}</h3>
                          <p className="text-sm text-gray-600 mb-4">
                            {note.content.length > 100 
                              ? `${note.content.substring(0, 100)}...` 
                              : note.content}
                          </p>
                          {note.group && (
                            <span className="inline-block bg-white/50 rounded-full px-3 py-1 text-sm mb-2">
                              {note.group}
                            </span>
                          )}
                          <div className="flex justify-between items-center text-sm text-gray-500">
                            <span>
                              {note.updated_at && 
                                `Updated ${formatDistanceToNow(new Date(note.updated_at))} ago`}
                            </span>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(note);
                                }}
                              >
                                <Share2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(note.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  )}
                </Draggable>
              );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {selectedNote && (
        <ShareNoteDialog
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          note={selectedNote}
        />
      )}
    </>
  );
};

export default DraggableNoteList;
